/**
 * DeliveredMetricsStrip
 *
 * A launched campaign's real delivery, above the plan summary on the review
 * pane. Everything else on that pane is sourced from the launch/import PLAN —
 * impression goal, plan budget, parsed audience count — which for a campaign
 * that is already live is either absent or stale. So opening a campaign from
 * the Campaign Grid (where the row was full of numbers) landed on a page of
 * '—' tiles.
 *
 * The figures are the SAME cached rows the grid reads
 * (GET /api/v2/campaign-grid/:id/delivered-metrics → campaign_grid_metrics),
 * formatted through the shared utils/metricFormat so the two surfaces can't
 * disagree about a campaign's spend.
 *
 * Renders nothing when the grid has never pulled the campaign — a campaign we
 * have not measured is not a campaign that delivered zero.
 */
import React from 'react';
import { formatRefreshedAgo } from '../../utils/refreshedLabel';
import { cur, num, numFlex, rate } from '../../utils/metricFormat';

/** Tiles for one metrics row, in the grid's Overview column order. */
export function deliveredTiles(metrics) {
  if (!metrics) return [];
  const code = metrics.currency_code || null;
  return [
    { label: 'Spend', value: cur(metrics.spend, code) },
    { label: 'Impressions', value: num(metrics.impressions) },
    { label: 'Clicks', value: num(metrics.clicks) },
    { label: 'Conversions', value: numFlex(metrics.conversions) },
    // Same fallback chain as the grid's KPI cell: the resolved KPI actual, else
    // plain CTR, labelled with whatever KPI the campaign is measured on.
    { label: metrics.kpi_label || 'CTR', value: rate(metrics.kpi_actual ?? metrics.ctr) },
  ];
}

export function DeliveredMetricsStrip({ metrics }) {
  const tiles = deliveredTiles(metrics);
  if (tiles.length === 0) return null;
  // Staleness is stated, not implied: these are cached figures refreshed on the
  // grid's 4-hourly pull, so a reviewer has to be able to see how old they are.
  // formatRefreshedAgo takes epoch ms; fetched_at arrives as an ISO timestamp.
  const fetchedAtMs = (() => {
    const t = new Date(metrics.fetched_at || 0).getTime();
    return Number.isFinite(t) && t > 0 ? t : null;
  })();
  const refreshed = formatRefreshedAgo(fetchedAtMs);

  return (
    // Deliberately NOT .clw-ps-breakdown: the review pane compacts that card's
    // cells assuming label-then-value markup, which would invert these tiles'
    // typography (a 9.5px spend under a 12px label).
    <div className="clw-delivered">
      <div className="clw-delivered-head">
        Delivered to date
        {refreshed && <span className="clw-delivered-ago">{refreshed}</span>}
      </div>
      <div className="clw-ps-stat-grid clw-delivered-grid">
        {tiles.map((t) => (
          <div className="clw-ps-stat" key={t.label}>
            <div className="clw-ps-stat-val">{t.value}</div>
            <div className="clw-ps-stat-lbl">{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DeliveredMetricsStrip;
